// Runtime configuration service for resolving backend URL at runtime
// Provides a minimal API used across the app: initialize() and getBackendUrl()

import { appConfig } from '../config/appConfig';

interface BackendConfigResponse {
  backendUrl?: string | null;
  source?: string;
  timestamp?: string;
}

class RuntimeConfigService {
  private initialized: boolean = false;
  private initializing: boolean = false;
  private backendUrl: string | undefined;

  async initialize(): Promise<void> {
    if (this.initialized || this.initializing) return;
    this.initializing = true;
    try {
      // 1) Hard override via environment (preferred in production builds)
      const envUrl = process.env.REACT_APP_API_URL;
      if (envUrl && typeof envUrl === 'string' && envUrl.trim().length > 0) {
        this.backendUrl = envUrl.trim();
        this.initialized = true;
        return;
      }

      // 2) Attempt dynamic discovery from backend (if same-origin proxy is available)
      try {
        const res = await fetch('/api/config/backend', { headers: { 'Accept': 'application/json' } });
        if (res.ok) {
          const data = (await res.json()) as BackendConfigResponse;
          if (data && data.backendUrl) {
            this.backendUrl = data.backendUrl;
            this.initialized = true;
            return;
          }
        }
      } catch {}

      // 3) Fallback to static app configuration
      if (!this.backendUrl) {
        this.backendUrl = appConfig.api.baseUrl || '';
      }
      this.initialized = true;
    } finally {
      this.initializing = false;
    }
  }

  getBackendUrl(): string | undefined {
    // Return discovered/configured URL, or fallback to appConfig
    return this.backendUrl || appConfig.api.baseUrl || undefined;
  }
}

export const runtimeConfig = new RuntimeConfigService();


